import * as React from 'react'
import { ChevronRight, Loader2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import type { ConnectorManifest } from '../connectorRegistry'
import { ConnectorIcon } from './connectorIcon'

export interface ConnectorPickerProps {
  connectors: ReadonlyArray<ConnectorManifest>
  selectedId?: string
  onSelect: (manifest: ConnectorManifest) => void
  /** Optional title shown above the grid. */
  title?: string
  /** Optional subtitle / explanation. */
  subtitle?: string
  /** Compact mode for narrow, first-run import surfaces. */
  compact?: boolean
  /** Connector manifests are still being resolved against rollout flags. */
  loading?: boolean
  /** Called for unavailable providers that should open a sales-contact flow. */
  onContactSales?: (manifest: ConnectorManifest) => void
  /** Sales-interest ID currently being delivered. */
  contactSalesPendingId?: string
}

const STATUS_BADGE: Record<
  ConnectorManifest['status'],
  { label: string; variant: 'outline' | 'secondary'; className?: string } | null
> = {
  stable: null,
  beta: {
    label: 'Beta',
    variant: 'outline',
    className: 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300',
  },
  'coming-soon': {
    label: 'Coming soon',
    variant: 'secondary',
  },
  hidden: null,
}

const PEOPLE_SYSTEMS = new Set([
  'azure_ad',
  'bamboohr',
  'google_workspace',
  'hibob',
  'workday',
])

const SPREADSHEET_SYSTEMS = new Set([
  'csv',
  'excel',
  'google_sheets',
  'spreadsheet',
])

const GROUP_DEFINITIONS = [
  {
    id: 'files',
    label: 'Files & spreadsheets',
    description: 'Upload a file or connect a spreadsheet you already use.',
  },
  {
    id: 'people',
    label: 'HR & directory systems',
    description: 'Bring in people, positions, and reporting relationships.',
  },
  {
    id: 'systems',
    label: 'Connected systems',
    description: 'Connect another system that feeds this folder.',
  },
] as const

type ConnectorGroupId = (typeof GROUP_DEFINITIONS)[number]['id']

function connectorGroup(manifest: ConnectorManifest): ConnectorGroupId {
  const system = manifest.source?.system
  if (system && PEOPLE_SYSTEMS.has(system)) return 'people'
  if (
    manifest.format === 'file' ||
    (system && SPREADSHEET_SYSTEMS.has(system))
  ) {
    return 'files'
  }
  return 'systems'
}

/**
 * Dashboard-native source gallery. Connector availability still comes from
 * the registry; this component only groups the filtered manifests and renders
 * them with shared shadcn surfaces and design tokens.
 */
export const ConnectorPicker: React.FC<ConnectorPickerProps> = ({
  connectors,
  selectedId,
  onSelect,
  title,
  subtitle,
  compact = false,
  loading = false,
  onContactSales,
  contactSalesPendingId,
}) => {
  const visible = connectors.filter((connector) => connector.status !== 'hidden')
  const groups = GROUP_DEFINITIONS.map((definition) => ({
    ...definition,
    connectors: visible.filter(
      (connector) => connectorGroup(connector) === definition.id,
    ),
  })).filter((group) => group.connectors.length > 0)

  return (
    <div
      className={cn('flex flex-col', compact ? 'gap-4' : 'gap-6')}
      data-testid="tid.compass.connectorPicker"
    >
      {title ? <h3 className="text-sm font-semibold text-foreground">{title}</h3> : null}
      {subtitle ? <p className="text-sm text-muted-foreground">{subtitle}</p> : null}
      {loading ? (
        <Card
          role="status"
          className="flex-row items-center justify-center gap-2 border-dashed bg-muted/30 p-8 text-sm text-muted-foreground shadow-none"
        >
          <Loader2 className="size-4 animate-spin" />
          Loading available sources…
        </Card>
      ) : visible.length === 0 ? (
        <Card className="border-dashed bg-muted/30 p-8 text-center text-sm text-muted-foreground shadow-none">
          No connectors are available for this folder yet.
        </Card>
      ) : (
        groups.map((group) => (
          <section
            key={group.id}
            aria-labelledby={`connector-group-${group.id}`}
            data-testid={`tid.compass.connectorPicker.group.${group.id}`}
          >
            <div className="mb-3 flex items-end justify-between gap-4">
              <div className="min-w-0">
                <h3
                  id={`connector-group-${group.id}`}
                  className="text-sm font-semibold text-foreground"
                >
                  {group.label}
                </h3>
                {!compact ? (
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {group.description}
                  </p>
                ) : null}
              </div>
              <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                {group.connectors.length}
              </span>
            </div>

            <div
              className={cn(
                'grid grid-cols-1 gap-3',
                !compact && 'sm:grid-cols-2 lg:grid-cols-3',
              )}
            >
              {group.connectors.map((manifest) => {
                const isSelected = manifest.id === selectedId
                const isContactSales =
                  manifest.status === 'coming-soon' &&
                  Boolean(manifest.salesInterestId) &&
                  Boolean(onContactSales)
                const isDisabled =
                  manifest.status === 'coming-soon' && !isContactSales
                const isContactSalesPending = Boolean(manifest.salesInterestId) && manifest.salesInterestId === contactSalesPendingId
                const badge = isContactSales ? null : STATUS_BADGE[manifest.status]

                return (
                  <Card
                    key={manifest.id}
                    className={cn(
                      'gap-0 overflow-hidden py-0 shadow-none transition-[border-color,box-shadow,background-color]',
                      'hover:border-primary/30 hover:shadow-sm',
                      isSelected && 'border-primary bg-primary/5 ring-2 ring-primary/20',
                      isDisabled && 'bg-muted/35 hover:border-border hover:shadow-none',
                    )}
                  >
                    <Button
                      type="button"
                      variant="ghost"
                      disabled={isDisabled || isContactSalesPending}
                      onClick={() => {
                        if (isContactSales) {
                          onContactSales?.(manifest)
                          return
                        }
                        onSelect(manifest)
                      }}
                      aria-pressed={isSelected}
                      aria-label={`Select ${manifest.label} connector`}
                      data-testid={`tid.compass.connectorPicker.option.${manifest.id}`}
                      className={cn(
                        'group h-full min-h-32 w-full items-start justify-start whitespace-normal rounded-xl p-4 text-left',
                        'hover:bg-accent/60 focus-visible:ring-inset',
                        'disabled:cursor-not-allowed disabled:opacity-100',
                      )}
                    >
                      <span className="flex size-11 shrink-0 items-center justify-center rounded-lg border border-border/70 bg-background p-2 shadow-xs">
                        <ConnectorIcon manifest={manifest} className="size-7" />
                      </span>

                      <span className="flex min-w-0 flex-1 flex-col self-stretch">
                        <span className="flex min-w-0 items-start justify-between gap-2">
                          <span className="truncate text-sm font-semibold text-foreground">
                            {manifest.label}
                          </span>
                          {badge ? (
                            <Badge
                              variant={badge.variant}
                              className={cn('shrink-0 text-[10px]', badge.className)}
                            >
                              {badge.label}
                            </Badge>
                          ) : null}
                        </span>
                        <span className="mt-1 line-clamp-2 text-xs leading-5 text-muted-foreground">
                          {manifest.description}
                        </span>
                        {!isDisabled ? (
                          <span className="mt-auto flex items-center gap-1 pt-3 text-xs font-medium text-primary">
                            {isContactSalesPending ? 'Contacting sales…' : isContactSales ? 'Contact sales' : 'Continue'}
                            <ChevronRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
                          </span>
                        ) : null}
                      </span>
                    </Button>
                  </Card>
                )
              })}
            </div>
          </section>
        ))
      )}
    </div>
  )
}
